import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { tokens } from '../../theme';
import ConnString from '../../ConnString.js';

const Connstring2 = ConnString.connstring;

const formatTinValue = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 12);
  const parts = [];

  for (let index = 0; index < digits.length; index += 3) {
    parts.push(digits.slice(index, index + 3));
  }

  return parts.join('-');
};

const getNextClientCode = (clientRows) => {
  const nextValue = clientRows.reduce((maxValue, row) => {
    const currentValue = Number.parseInt(String(row?.CompCode ?? row?.compCode ?? '').trim(), 10);
    return Number.isFinite(currentValue) && currentValue > maxValue ? currentValue : maxValue;
  }, 0) + 1;

  return String(nextValue);
};

const stripTinDigits = (value) => String(value || '').replace(/\D/g, '').slice(0, 12);

const formatDateValue = (value) => {
  if (!value) return '—';

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return String(value);

  return parsedDate.toLocaleDateString();
};

const dateInputValue = (value) => {
  const matchedDate = String(value || '').match(/^(\d{4}-\d{2}-\d{2})/);
  return matchedDate?.[1] || '';
};

const TlClientProfile = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [codeFilter, setCodeFilter] = useState('');
  const [tinFilter, setTinFilter] = useState('');
  const [sortField, setSortField] = useState('companyName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [newClientOpen, setNewClientOpen] = useState(false);
  const [newClientMode, setNewClientMode] = useState('create');
  const [newClientTouched, setNewClientTouched] = useState(false);
  const [newClientSaving, setNewClientSaving] = useState(false);
  const [newClientError, setNewClientError] = useState('');
  const [newClientCode, setNewClientCode] = useState('');
  const [newClientName, setNewClientName] = useState('');
  const [newClientTin, setNewClientTin] = useState('');
  const [newClientRemarks, setNewClientRemarks] = useState('');
  const [newClientPersonInCharge, setNewClientPersonInCharge] = useState('');
  const [newClientPersonInChargeName, setNewClientPersonInChargeName] = useState('');
  const [newClientProfileDate, setNewClientProfileDate] = useState('');
  const [personInChargeOptions, setPersonInChargeOptions] = useState([]);
  const [personInChargeOptionsLoading, setPersonInChargeOptionsLoading] = useState(false);
  const [viewClientOpen, setViewClientOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedClientDetails, setSelectedClientDetails] = useState(null);
  const [viewClientLoading, setViewClientLoading] = useState(false);
  const [filingsOpen, setFilingsOpen] = useState(false);
  const [filingsLoading, setFilingsLoading] = useState(false);
  const [filingsError, setFilingsError] = useState('');
  const [filingsRows, setFilingsRows] = useState([]);
  const [filingsClient, setFilingsClient] = useState(null);
  const [returnOptions, setReturnOptions] = useState([]);
  const [assignmentReturn, setAssignmentReturn] = useState('');
  const [assignmentDateStart, setAssignmentDateStart] = useState('');
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  const [assignmentError, setAssignmentError] = useState('');
  const [assignmentTouched, setAssignmentTouched] = useState(false);
  const [compID] = useState(() => sessionStorage.getItem('compID'));
  const selectedCompID = compID || sessionStorage.getItem('compID');

  const clients = useMemo(() => {
    return rows.map((row, index) => ({
      id: row.id ?? row.CompCode ?? index,
      compCode: String(row.CompCode ?? '—'),
      companyName: String(row.Companyname ?? row.CompanyName ?? '—'),
      compTIN: String(row.CompTIN ?? '—'),
      remarks: String(row.Remarks ?? '—'),
      personInCharge: String(row.PersonInCharge ?? ''),
      personInChargeName: String(row.PersonInChargeName ?? row.PersonInCharge ?? ''),
      dateOfBirthIncorporation: row.DateOfBirthIncorporation ?? null,
      returnCount: Number(row.ReturnCount ?? 0),
    }));
  }, [rows]);

  const filteredClients = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    const code = codeFilter.trim().toLowerCase();
    const tin = tinFilter.trim().toLowerCase();

    return clients.filter((client) => {
      const matchesSearch = !search || [
        client.compCode,
        client.companyName,
        client.compTIN,
        client.remarks,
        client.personInCharge,
        client.personInChargeName,
        formatDateValue(client.dateOfBirthIncorporation),
      ]
        .some((value) => value.toLowerCase().includes(search));
      const matchesCode = !code || client.compCode.toLowerCase().includes(code);
      const matchesTin = !tin || client.compTIN.toLowerCase().includes(tin);

      return matchesSearch && matchesCode && matchesTin;
    });
  }, [clients, codeFilter, searchText, tinFilter]);

  const sortedClients = useMemo(() => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    return [...filteredClients].sort((left, right) => {
      const leftValue = String(left?.[sortField] ?? '').toLowerCase();
      const rightValue = String(right?.[sortField] ?? '').toLowerCase();

      if (leftValue < rightValue) return -1 * direction;
      if (leftValue > rightValue) return 1 * direction;
      return 0;
    });
  }, [filteredClients, sortDirection, sortField]);

  const fetchData = useCallback(async () => {
    if (!selectedCompID) {
      setRows([]);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${Connstring2}/api/ClientProfiles`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyID: selectedCompID }),
      });

      const data = await response.json();

      if (!data?.success || !Array.isArray(data.clients)) {
        setRows([]);
        return;
      }

      const transformedRows = data.clients.map((row, index) => ({
        ...row,
        id: row.id ?? row.CompCode ?? index,
      }));

      setRows(transformedRows);
    } catch (error) {
      console.error('Error fetching client data:', error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCompID]);

  const fetchPersonInChargeOptions = useCallback(async () => {
    if (!selectedCompID) {
      setPersonInChargeOptions([]);
      return;
    }

    setPersonInChargeOptionsLoading(true);

    try {
      const response = await fetch(`${Connstring2}/api/ClientProfiles/PersonInChargeOptions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyID: selectedCompID }),
      });
      const data = await response.json();

      setPersonInChargeOptions(response.ok && data?.success && Array.isArray(data.options) ? data.options : []);
    } catch (error) {
      console.error('Error fetching person-in-charge options:', error);
      setPersonInChargeOptions([]);
    } finally {
      setPersonInChargeOptionsLoading(false);
    }
  }, [selectedCompID]);

  useEffect(() => {
    fetchData();
    fetchPersonInChargeOptions();
  }, [fetchData, fetchPersonInChargeOptions]);

  const handleClearFilters = () => {
    setSearchText('');
    setCodeFilter('');
    setTinFilter('');
    setSortField('companyName');
    setSortDirection('asc');
  };

  const handleOpenViewClient = async (client) => {
    setSelectedClient(client);
    setViewClientOpen(true);
    setSelectedClientDetails(client);
    setViewClientLoading(true);

    try {
      const response = await fetch(`${Connstring2}/api/ClientProfiles/Details`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyID: selectedCompID, compCode: client.compCode }),
      });

      const data = await response.json();

      if (response.ok && data?.success && data.client) {
        setSelectedClientDetails(data.client);
      }
    } catch (error) {
      console.error('Error fetching client details:', error);
    } finally {
      setViewClientLoading(false);
    }
  };

  const handleCloseViewClient = () => {
    setViewClientOpen(false);
    setSelectedClient(null);
    setSelectedClientDetails(null);
  };

  const handleOpenFilings = async (client) => {
    setFilingsClient(client);
    setFilingsOpen(true);
    setFilingsLoading(true);
    setFilingsError('');
    setFilingsRows([]);

    try {
      const response = await fetch(`${Connstring2}/api/ClientProfiles/Filings`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyID: selectedCompID, compCode: client.compCode }),
      });

      const data = await response.json();

      if (!response.ok || !data?.success) {
        setFilingsError(data?.message || 'Unable to load filing details.');
        return;
      }

      setFilingsRows(Array.isArray(data?.filings) ? data.filings : []);

      const optionsResponse = await fetch(`${Connstring2}/api/ClientProfiles/Filings/ReturnOptions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyID: selectedCompID }),
      });

      const optionsData = await optionsResponse.json();
      if (optionsResponse.ok && optionsData?.success) {
        setReturnOptions(Array.isArray(optionsData?.returnOptions) ? optionsData.returnOptions : []);
      } else {
        setReturnOptions([]);
      }
    } catch (error) {
      console.error('Error fetching filing details:', error);
      setFilingsError('Unable to load filing details due to network/server error.');
    } finally {
      setFilingsLoading(false);
    }
  };

  const handleCloseFilings = () => {
    setFilingsOpen(false);
    setFilingsLoading(false);
    setFilingsError('');
    setFilingsRows([]);
    setFilingsClient(null);
    setReturnOptions([]);
    setAssignmentReturn('');
    setAssignmentDateStart('');
    setAssignmentSaving(false);
    setAssignmentError('');
    setAssignmentTouched(false);
  };

  const loadFilingsForClient = async (client) => {
    const response = await fetch(`${Connstring2}/api/ClientProfiles/Filings`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyID: selectedCompID, compCode: client.compCode }),
    });

    const data = await response.json();

    if (!response.ok || !data?.success) {
      throw new Error(data?.message || 'Unable to load filing details.');
    }

    setFilingsRows(Array.isArray(data?.filings) ? data.filings : []);
  };

  const isAssignmentValid = Boolean(assignmentReturn.trim() && assignmentDateStart);

  const handleCreateAssignment = async () => {
    setAssignmentTouched(true);
    if (!isAssignmentValid || !filingsClient?.compCode) {
      return;
    }

    setAssignmentSaving(true);
    setAssignmentError('');

    try {
      const response = await fetch(`${Connstring2}/api/ClientProfiles/Filings/CreateAssignment`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyID: selectedCompID,
          compCode: filingsClient.compCode,
          returnx: assignmentReturn.trim(),
          dateStart: assignmentDateStart,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        setAssignmentError(data?.message || 'Unable to create assignment.');
        return;
      }

      setAssignmentReturn('');
      setAssignmentDateStart('');
      setAssignmentTouched(false);
      await loadFilingsForClient(filingsClient);
    } catch (error) {
      console.error('Error creating filing assignment:', error);
      setAssignmentError('Unable to create assignment due to network/server error.');
    } finally {
      setAssignmentSaving(false);
    }
  };

  const handleSetAssignmentActive = async (row, shouldBeActive) => {
    if (!filingsClient?.compCode || !row?.Returnx) {
      return;
    }

    if (!shouldBeActive) {
      const isConfirmed = window.confirm(`Are you sure you want to deactivate return ${row.Returnx} for company ${filingsClient.compCode}?`);
      if (!isConfirmed) {
        return;
      }
    }

    setAssignmentError('');

    try {
      const response = await fetch(`${Connstring2}/api/ClientProfiles/Filings/SetActive`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyID: selectedCompID,
          compCode: filingsClient.compCode,
          returnx: row.Returnx,
          isActive: shouldBeActive,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        setAssignmentError(data?.message || 'Unable to update assignment status.');
        return;
      }

      await loadFilingsForClient(filingsClient);
    } catch (error) {
      console.error('Error updating assignment status:', error);
      setAssignmentError('Unable to update assignment status due to network/server error.');
    }
  };

  const resetNewClientForm = () => {
    setNewClientCode('');
    setNewClientName('');
    setNewClientTin('');
    setNewClientRemarks('');
    setNewClientPersonInCharge('');
    setNewClientPersonInChargeName('');
    setNewClientProfileDate('');
    setNewClientTouched(false);
    setNewClientError('');
  };

  const handleOpenNewClient = () => {
    setNewClientMode('create');
    resetNewClientForm();
    setNewClientCode(getNextClientCode(rows));
    setNewClientOpen(true);
  };

  const handleOpenEditClient = (client) => {
    setNewClientMode('edit');
    setNewClientCode(String(client?.compCode ?? client?.CompCode ?? ''));
    setNewClientName(String(client?.companyName ?? client?.Companyname ?? client?.CompanyName ?? ''));
    setNewClientTin(stripTinDigits(client?.compTIN ?? client?.CompTIN ?? ''));
    setNewClientRemarks(String(client?.remarks ?? client?.Remarks ?? ''));
    setNewClientPersonInCharge(String(client?.personInCharge ?? client?.PersonInCharge ?? ''));
    setNewClientPersonInChargeName(String(client?.personInChargeName ?? client?.PersonInChargeName ?? client?.PersonInCharge ?? ''));
    setNewClientProfileDate(dateInputValue(client?.dateOfBirthIncorporation ?? client?.DateOfBirthIncorporation));
    setNewClientTouched(false);
    setNewClientError('');
    setNewClientOpen(true);
    setViewClientOpen(false);
  };

  const handleCloseNewClient = () => {
    setNewClientOpen(false);
  };

  const isNewClientValid = Boolean(newClientCode.trim() && newClientName.trim());

  const handleCreateClient = async () => {
    setNewClientTouched(true);
    if (!isNewClientValid) {
      return;
    }

    setNewClientSaving(true);
    setNewClientError('');

    try {
      const response = await fetch(`${Connstring2}/api/ClientProfiles/${newClientMode === 'edit' ? 'Update' : 'Create'}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyID: selectedCompID,
          CompCode: newClientCode.trim(),
          Companyname: newClientName.trim(),
          CompTIN: formatTinValue(newClientTin),
          Remarks: newClientRemarks.trim(),
          PersonInCharge: newClientPersonInCharge,
          DateOfBirthIncorporation: newClientProfileDate || null,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data?.success) {
        setNewClientError(data?.message || 'Unable to create client profile.');
        return;
      }

      setNewClientOpen(false);
      await fetchData();
    } catch (error) {
      console.error('Error creating client profile:', error);
      setNewClientError('Unable to save client profile due to network/server error.');
    } finally {
      setNewClientSaving(false);
    }
  };

  const summaryCards = [
    { label: 'Total Clients', value: clients.length, accent: colors.blueAccent?.[500] ?? '#6870fa' },
    { label: 'Visible', value: filteredClients.length, accent: colors.greenAccent?.[500] ?? '#4cceac' },
    { label: 'Assigned PIC', value: clients.filter((client) => client.personInCharge).length, accent: colors.blueAccent?.[300] ?? '#60a5fa' },
    { label: 'With Remarks', value: clients.filter((client) => client.remarks && client.remarks !== '—').length, accent: colors.redAccent?.[400] ?? '#e2726e' },
  ];

  const selectedPersonInChargeOption = newClientPersonInCharge
    ? personInChargeOptions.find((option) => option.Name === newClientPersonInCharge) || {
        Name: newClientPersonInChargeName || newClientPersonInCharge,
      }
    : null;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, minHeight: '100vh', bgcolor: theme.palette.mode === 'dark' ? '#0f172a' : '#f8fafc' }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          borderRadius: 4,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(14,116,144,0.95), rgba(59,130,246,0.9))'
            : 'linear-gradient(135deg, #2563eb, #38bdf8)',
          color: '#fff',
          boxShadow: '0 20px 45px rgba(37, 99, 235, 0.24)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Clients
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
              Monitor client assignments, profile dates, filing setup, and core company information.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            <Button variant="outlined" startIcon={<RefreshOutlinedIcon />} onClick={fetchData} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' } }}>
              Refresh
            </Button>
            <Button variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={handleOpenNewClient} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' } }}>
              New Client
            </Button>
            <Button variant="contained" startIcon={<FilterAltOutlinedIcon />} onClick={handleClearFilters} sx={{ bgcolor: '#fff', color: '#2563eb', '&:hover': { bgcolor: '#f8fafc' } }}>
              Clear Filters
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        {summaryCards.map((card) => (
          <Paper key={card.label} elevation={0} sx={{ flex: 1, p: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)'}` }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{card.label}</Typography>
            <Typography variant="h4" fontWeight={700} sx={{ color: card.accent }}>
              {card.value}
            </Typography>
          </Paper>
        ))}
      </Stack>

      <Paper elevation={0} sx={{ borderRadius: 4, border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)'}`, overflow: 'hidden' }}>
        <Box sx={{ p: { xs: 2, md: 3 }, borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)'}` }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', lg: 'center' }}>
            {/* <Box>
              <Typography variant="h5" fontWeight={700}>Client List</Typography>
              <Typography variant="body2" color="text.secondary">Showing company code, client name, TIN, and remarks.</Typography>
            </Box> */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <TextField
                size="small"
                label="Search"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Search name, code, TIN, PIC, remarks"
                InputProps={{ startAdornment: <SearchOutlinedIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                sx={{ minWidth: { xs: '100%', sm: 260 } }}
              />
              <TextField
                size="small"
                label="Comp Code"
                value={codeFilter}
                onChange={(event) => setCodeFilter(event.target.value)}
                sx={{ minWidth: { xs: '100%', sm: 140 } }}
              />
              <TextField
                size="small"
                label="TIN"
                value={tinFilter}
                onChange={(event) => setTinFilter(event.target.value)}
                sx={{ minWidth: { xs: '100%', sm: 180 } }}
              />
              <TextField
                select
                size="small"
                label="Sort By"
                value={sortField}
                onChange={(event) => setSortField(event.target.value)}
                sx={{ minWidth: { xs: '100%', sm: 150 } }}
              >
                <MenuItem value="companyName">Name</MenuItem>
                <MenuItem value="compCode">Code</MenuItem>
                <MenuItem value="compTIN">TIN</MenuItem>
                <MenuItem value="personInChargeName">Person-in-Charge</MenuItem>
                <MenuItem value="dateOfBirthIncorporation">Birth / Incorporation Date</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label="Order"
                value={sortDirection}
                onChange={(event) => setSortDirection(event.target.value)}
                sx={{ minWidth: { xs: '100%', sm: 140 } }}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </TextField>
              <Chip label={`${sortedClients.length} items`} color="primary" variant="outlined" />
            </Stack>
          </Stack>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : sortedClients.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={600}>No clients found</Typography>
            <Typography variant="body2" color="text.secondary">Try adjusting your search text or filters.</Typography>
          </Box>
        ) : (
          <Stack spacing={2} sx={{ p: { xs: 2, md: 3 } }}>
            {sortedClients.map((client) => (
              <Paper key={client.id} variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.08)' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                      <Typography variant="h6" fontWeight={700}>{client.companyName}</Typography>
                      <Chip label={client.compCode} size="small" color="primary" variant="outlined" />
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ color: 'text.secondary' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessOutlinedIcon fontSize="small" />
                        <Typography variant="body2">Company Code: {client.compCode}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BadgeOutlinedIcon fontSize="small" />
                        <Typography variant="body2">TIN: {client.compTIN}</Typography>
                      </Box>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ color: 'text.secondary', mt: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonOutlineOutlinedIcon fontSize="small" />
                        <Typography variant="body2">PIC: {client.personInChargeName || 'Not assigned'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarMonthOutlinedIcon fontSize="small" />
                        <Typography variant="body2">
                          Birth / Incorporation: {formatDateValue(client.dateOfBirthIncorporation)}
                        </Typography>
                      </Box>
                    </Stack>
                    {client.remarks && client.remarks !== '—' && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Remarks: {client.remarks}
                      </Typography>
                    )}
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ borderRadius: 999, px: 2 }}
                    onClick={() => handleOpenViewClient(client)}
                  >
                    View
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ borderRadius: 999, px: 2 }}
                    onClick={() => handleOpenEditClient(client)}
                  >
                    Edit
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ borderRadius: 999, px: 2 }}
                    onClick={() => handleOpenFilings(client)}
                  >
                    Filings ({client.returnCount})
                  </Button>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>

      <Dialog open={newClientOpen} onClose={handleCloseNewClient} fullWidth maxWidth="md">
        <DialogTitle>{newClientMode === 'edit' ? 'Edit Client Profile' : 'Create New Client Profile'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Company Code"
              value={newClientCode}
              InputProps={{ readOnly: true }}
              required
              helperText={newClientMode === 'edit' ? 'Company Code cannot be changed' : 'Generated automatically from the current client list'}
            />
            <TextField
              label="Company Name"
              value={newClientName}
              onChange={(event) => setNewClientName(event.target.value)}
              required
              error={newClientTouched && !newClientName.trim()}
              helperText={newClientTouched && !newClientName.trim() ? 'Company Name is required' : ''}
            />
            <TextField
              label="TIN"
              value={formatTinValue(newClientTin)}
              onChange={(event) => setNewClientTin(stripTinDigits(event.target.value))}
              placeholder="000-000-000-000"
              inputProps={{ inputMode: 'numeric' }}
              helperText="Enter up to 12 digits; it will display as 000-000-000-000"
            />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Autocomplete
                fullWidth
                options={personInChargeOptions}
                value={selectedPersonInChargeOption}
                loading={personInChargeOptionsLoading}
                onChange={(_, option) => {
                  setNewClientPersonInCharge(option?.Name || '');
                  setNewClientPersonInChargeName(option?.Name || '');
                }}
                getOptionLabel={(option) => String(option?.Name || '').trim()}
                isOptionEqualToValue={(option, value) => option.Name === value.Name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Person-in-Charge"
                    helperText="Optional — assign an active person responsible for this client"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {personInChargeOptionsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <TextField
                fullWidth
                label="Date of Birth / Incorporation"
                type="date"
                value={newClientProfileDate}
                onChange={(event) => setNewClientProfileDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Use birth date for individuals or incorporation date for organizations"
              />
            </Stack>
            <TextField
              label="Remarks"
              value={newClientRemarks}
              onChange={(event) => setNewClientRemarks(event.target.value)}
              multiline
              minRows={3}
            />
            {newClientError && (
              <Typography variant="body2" color="error">
                {newClientError}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseNewClient} disabled={newClientSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateClient} disabled={newClientSaving}>
            {newClientSaving ? 'Saving...' : newClientMode === 'edit' ? 'Update Client' : 'Save Client'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={viewClientOpen} onClose={handleCloseViewClient} fullWidth maxWidth="sm">
        <DialogTitle>Client Details</DialogTitle>
        <DialogContent>
          {viewClientLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Typography variant="body2"><strong>Company Name:</strong> {selectedClientDetails?.Companyname || selectedClient?.companyName || '—'}</Typography>
              <Typography variant="body2"><strong>Company Code:</strong> {selectedClientDetails?.CompCode || selectedClient?.compCode || '—'}</Typography>
              <Typography variant="body2"><strong>TIN:</strong> {selectedClientDetails?.CompTIN || selectedClient?.compTIN || '—'}</Typography>
              <Typography variant="body2"><strong>Person-in-Charge:</strong> {selectedClientDetails?.PersonInChargeName || selectedClientDetails?.PersonInCharge || selectedClient?.personInChargeName || 'Not assigned'}</Typography>
              <Typography variant="body2"><strong>Date of Birth / Incorporation:</strong> {formatDateValue(selectedClientDetails?.DateOfBirthIncorporation || selectedClient?.dateOfBirthIncorporation)}</Typography>
              <Typography variant="body2"><strong>Remarks:</strong> {selectedClientDetails?.Remarks || selectedClient?.remarks || '—'}</Typography>
              {Object.entries(selectedClientDetails || {})
                .filter(([key]) => !['CompCode', 'Companyname', 'CompanyName', 'CompTIN', 'PersonInCharge', 'PersonInChargeName', 'DateOfBirthIncorporation', 'Remarks', 'id'].includes(key))
                .filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== '')
                .map(([key, value]) => (
                  <Typography key={key} variant="body2">
                    <strong>{key}:</strong> {String(value)}
                  </Typography>
                ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => handleOpenEditClient(selectedClientDetails || selectedClient)} disabled={viewClientLoading || !selectedClient}>Edit</Button>
          <Button onClick={handleCloseViewClient}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={filingsOpen} onClose={handleCloseFilings} fullWidth maxWidth="md">
        <DialogTitle>Filing Details</DialogTitle>
        <DialogContent>
          {filingsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Typography variant="body2"><strong>Company Code:</strong> {filingsRows?.[0]?.CompCode || filingsClient?.compCode || '—'}</Typography>
              <Typography variant="body2"><strong>Company Name:</strong> {filingsRows?.[0]?.CompanyName || filingsClient?.companyName || '—'}</Typography>
              <Typography variant="body2"><strong>Company TIN:</strong> {filingsRows?.[0]?.CompTIN || filingsClient?.compTIN || '—'}</Typography>

              <Typography variant="subtitle2" sx={{ pt: 1 }}>
                Filing Details
              </Typography>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2">Create New Return Assignment</Typography>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                    <TextField
                      select
                      size="small"
                      label="Return"
                      value={assignmentReturn}
                      onChange={(event) => setAssignmentReturn(event.target.value)}
                      error={assignmentTouched && !assignmentReturn.trim()}
                      helperText={assignmentTouched && !assignmentReturn.trim() ? 'Return is required' : ''}
                      sx={{ flex: 1 }}
                    >
                      {returnOptions.map((option) => (
                        <MenuItem key={option.Return} value={option.Return}>
                          {option.Return} - {option.Descriptions}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      label="Date Start"
                      type="date"
                      value={assignmentDateStart}
                      onChange={(event) => setAssignmentDateStart(event.target.value)}
                      error={assignmentTouched && !assignmentDateStart}
                      helperText={assignmentTouched && !assignmentDateStart ? 'Date Start is required' : ''}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: { xs: '100%', md: 180 } }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleCreateAssignment}
                      disabled={assignmentSaving}
                      sx={{ minWidth: { xs: '100%', md: 140 } }}
                    >
                      {assignmentSaving ? 'Saving...' : 'Add Return'}
                    </Button>
                  </Stack>
                </Stack>
              </Paper>

              {assignmentError && (
                <Typography variant="body2" color="error">{assignmentError}</Typography>
              )}

              {filingsError ? (
                <Typography variant="body2" color="error">{filingsError}</Typography>
              ) : filingsRows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No filing assignments found for this company.</Typography>
              ) : (
                <Stack spacing={1}>
                  {filingsRows.map((row, index) => (
                    <Paper key={`${row.Returnx || 'Return'}-${index}`} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="body2"><strong>Return:</strong> {row.Returnx || '—'}</Typography>
                      <Typography variant="body2"><strong>Description:</strong> {row.Descriptions || '—'}</Typography>
                      <Typography variant="body2"><strong>Date Start:</strong> {formatDateValue(row.DateStart)}</Typography>
                      <Typography variant="body2"><strong>Status:</strong> {row.IsActive ? 'Active' : 'Inactive'}</Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          disabled={Boolean(row.IsActive)}
                          onClick={() => handleSetAssignmentActive(row, true)}
                        >
                          Activate
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          disabled={!Boolean(row.IsActive)}
                          onClick={() => handleSetAssignmentActive(row, false)}
                        >
                          Deactivate
                        </Button>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseFilings}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TlClientProfile;
